/* eslint-disable max-len */
import { useIntl } from 'react-intl';
import { CalendarIcon, BadgeCheckIcon, SearchIcon } from '@heroicons/react/solid';
import { SecondaryButton, SecondaryLinkButton } from '@luminalearning/components';
import luminaEnv from 'src/env';
import { snakeCaseToTitleCase } from 'src/utils/humanize';
import { useEffect, useState } from 'react';
import { Assertion } from 'src/types';
import { useLazyQuery, useMutation } from '@apollo/client';
import PRACTITIONER_BADGES from 'src/graphql/queries/practitionerBadges';
import GENERATE_BADGE from 'src/graphql/mutations/generateBadge';

type Props = {
    /** Bool to tell card if data is ready or still loading */
    isLoading: boolean;
    /** bool to tell if the user is qualified in the product. */
    isQualified: boolean;
    /** profile identifier. */
    practitionerId?: number;
    /** bool or object to tell if a badge is generated for the qualification. */
    badgesArray: Array<Assertion>;
    /** String name of the qualification */
    qualification: string;
    /** Image of the qualification */
    qualificationImage: React.ReactElement;
    /** String containing the date the practitioner qualifed */
    dateQualified: string;
    /** Class name to add colour of the qualification  */
    className: string
};

export const QualificationContainer = (props: Props) => {
    const [badgeResponse, setBadgeResponse] = useState<Assertion[]>([]);
    const [loadingText, setLoadingText] = useState<boolean>(false);
    const {
        isLoading,
        isQualified,
        practitionerId,
        badgesArray,
        qualification,
        qualificationImage,
        dateQualified,
        className,
    } = props;

    const intl = useIntl();

    const [generateBadge, {
        data: badgesData,
    }] = useMutation(GENERATE_BADGE, {});

    const [fetchBadges, {
        data: fetchBadgesData,
    }] = useLazyQuery(
        PRACTITIONER_BADGES,
        {
            variables: { practitionerId },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
        }
    );

    const handleGenerateBadge = (pracId: number | undefined, qualificationName: string) => {
        setLoadingText(true);
        generateBadge({ variables: { practitionerId: pracId, qualification: qualificationName } });
    };

    const isBadgeGenerated = (badges: Array<Assertion> | null, qualificationName: string): any => {
        if (badges == null) return false;
        const isBadgePresent = badges.find((el: any) => el.badgeClassName === qualificationName);
        return isBadgePresent || false;
    };

    const displayBadgeBtn = (badges: Array<Assertion> | null, qualificationName: string) => {
        if (isBadgeGenerated(badges, qualificationName)) {
            return <SecondaryLinkButton
                text='View Badge'
                icon={SearchIcon}
                href={`${luminaEnv.luminaOpenBadgesClientUrl}/badge_assertions/${isBadgeGenerated(badges, qualificationName).assertionGuid}`} target="_blank" rel="noreferrer" className='h-9 text-sm' />;
        }
        return <SecondaryButton text='Generate Badge' icon={BadgeCheckIcon}
            onClick={() => handleGenerateBadge(
                practitionerId, qualificationName
            )} isLoading={loadingText} className='h-9 text-sm' />;
    };

    useEffect(() => {
        if (badgesData && badgesData.generatePractitionerBadge) {
            fetchBadges();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [badgesData]);

    useEffect(() => {
        if (fetchBadgesData && fetchBadgesData.practitionerBadges) {
            setBadgeResponse(fetchBadgesData.practitionerBadges);
            setLoadingText(false);
        }
    }, [fetchBadgesData]);

    return (
        <div
            className={[
                'h-auto pb-4 flex w-qual flex-col items-center bg-white border-1-5 border-card-normal rounded-md mr-6 mb-6',
                (isLoading ? 'animate-pulse' : ''),
            ].join(' ')}
        >

            <div className='mt-4 mb-2'>
                <span className={`text-label-lg font-bold ${className}`}>
                    {snakeCaseToTitleCase(qualification)}
                </span>
            </div>
            {isQualified
                && <>
                    <div className='w-auto h-10 flex flex-row justify-center items-center
                                    border-2-5 border-qualified  p-alert rounded-md'>

                        <CalendarIcon className='fill-icon-qualified mr-4' height='1.5rem' />
                        {
                            intl.formatMessage({
                                id: 'LP_QUALIFIED',
                                defaultMessage: 'Qualified',
                            })
                        }
                        <span className='font-bold text-paragraph-reg ml-1'>
                            {dateQualified}
                        </span>

                    </div>
                    <div>
                        {qualificationImage}
                    </div>
                    <div className='flex flex-row justify-center items-center font-bold text-paragraph-reg'>
                        {badgeResponse.length !== 0 ? displayBadgeBtn(badgeResponse, qualification)
                            : displayBadgeBtn(badgesArray, qualification)}
                    </div>
                </>
            }
            {!isQualified
                && <>

                    <div className='w-auto h-10 flex flex-row justify-center
                        items-center border-2-5 border-unqualified  p-alert rounded-md'>
                        <CalendarIcon className='fill-icon-unqualified mr-4' height='1.5rem' />

                        {
                            intl.formatMessage({
                                id: 'LP_NOT_QUALIFIED',
                                defaultMessage: 'Not Qualified Yet',
                            })
                        }

                    </div>

                    <div>
                        {qualificationImage}
                    </div>
                    <div className='flex flex-row justify-center items-center font-bold text-paragraph-reg'>
                        <SecondaryLinkButton
                            direction="Right"
                            hasArrow={true}
                            href={luminaEnv.luminaGetQualifiedUrl}
                            className='h-9 text-sm' target='_blank' rel='noreferrer'>
                            {
                                intl.formatMessage({
                                    id: 'LP_GET_QUALIFIED',
                                    defaultMessage: 'Get Qualified',
                                })
                            }
                        </SecondaryLinkButton>
                    </div>
                </>
            }

        </div>

    );
};

export default QualificationContainer;